import { cn } from "@core/utils/cn";
import type { ComponentProps } from "react";
import type { IconType } from "react-icons";
import { NavLink } from "react-router-dom";

type SideBarElementProps = Omit<ComponentProps<typeof NavLink>, "className"> & {
  icon?: IconType | string;
  iconPosition?: "left" | "right";
  text: string;
  isActive?: boolean;
  badge?: number | string;
  collapsed?: boolean;
};

const SideBarElement = ({
  text,
  icon: Icon,
  isActive = false,
  iconPosition = "left",
  badge,
  collapsed = false,
  ...props
}: SideBarElementProps) => {
  const hasBadge = badge !== undefined && badge !== null && badge !== 0;

  if (collapsed) {
    return (
      <NavLink
        title={text}
        aria-label={text}
        className={cn(
          "relative flex items-center justify-center w-11 h-11 rounded-lg transition-all duration-200 ease-in-out focus:outline-none",
          !isActive &&
            "text-neutral-300 hover:bg-surface-hover/50 hover:text-neutral-950",
          isActive &&
            "border border-secondary-200 text-primary-800 bg-secondary-50",
        )}
        {...props}
      >
        {Icon && <Icon className="shrink-0 text-xl" />}
        {hasBadge && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent-red-600" />
        )}
      </NavLink>
    );
  }

  return (
    <NavLink
      className={cn(
        "relative flex items-center w-[90%] ml-4 gap-2 h-[2.4375rem] px-2 rounded transition-all duration-200 ease-in-out focus:outline-none overflow-hidden text-sm",
        !isActive &&
          "font-normal text-neutral-300 hover:bg-surface-hover/50 hover:text-neutral-950 hover:font-medium",
        isActive &&
          "font-medium border border-secondary-200 text-primary-800 bg-secondary-50",
      )}
      {...props}
    >
      {Icon && iconPosition === "left" && (
        <Icon className="shrink-0 text-xl 2xl:text-2xl" />
      )}
      <span className="2xl:text-base capitalize whitespace-nowrap flex-1">
        {text}
      </span>
      {badge !== undefined && badge !== null && (
        <>
          {typeof badge === "number" && badge > 0 && (
            <span className="ml-auto shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-md text-[10px] font-semibold bg-accent-red-50 text-accent-red-600">
              {badge > 99 ? "99+" : badge}
            </span>
          )}

          {typeof badge === "string" && (
            <span className="ml-auto shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-md text-[10px] font-semibold bg-accent-red-50 text-accent-red-600">
              {badge}
            </span>
          )}
        </>
      )}
      {Icon && iconPosition === "right" && (
        <Icon className="shrink-0 text-xl 2xl:text-2xl" />
      )}
    </NavLink>
  );
};

export default SideBarElement;
